import { Component, OnInit } from '@angular/core';
import { CommonModule, AsyncPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Store, select } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, switchMap, take, map } from 'rxjs/operators';
import {
  Settings, cellSizeOptions, AppTheme,
} from '@models/settings.model';
import { BUILTIN_PALETTES, ColorPalettePreset, CustomPalette } from '@models/palette.model';
import { AppState } from '@store/app.state';
import { SettingsActions } from '@store/actions/settings.actions';
import { selectSettings, selectIsSettingLoading } from '@store/selectors/settings.selector';
import { UserConfigActions } from '@store/actions/user-config.actions';
import { selectCanvasHintsExpanded } from '@store/selectors/user-config.selectors';

@Component({
  selector: 'menu-settings',
  standalone: true,
  imports: [CommonModule, AsyncPipe, FormsModule],
  templateUrl: './menu.settings.html',
  styleUrls: ['./menu.settings.css']
})
export class MenuSettings implements OnInit {
  undoDepthOptions = [
    { value: 20,  label: '20 steps (light)' },
    { value: 50,  label: '50 steps' },
    { value: 100, label: '100 steps' },
    { value: 200, label: '200 steps (heavy)' },
  ];
  fpsOptions = [6, 8, 12, 15, 24, 30];
  Math = Math;
  gridLineOptions: { value: number; label: string }[] = [
    { value: 0,   label: 'Off' },
    { value: 0.5, label: '0.5' },
    { value: 1,   label: '1px' },
    { value: 1.5, label: '1.5' },
    { value: 2,   label: '2px' },
    { value: 3,   label: '3px' },
  ];
  cellSizeOptions = cellSizeOptions;
  builtinPalettes: ColorPalettePreset[] = BUILTIN_PALETTES;
  themes: { value: AppTheme; label: string }[] = [
    { value: 'pxlvd', label: '🟢 pxlvd' },
    { value: 'dark',  label: '⚫ Dark' },
    { value: 'light', label: '⬜ Light' },
  ];

  selectedSetting!: Settings;
  isSaved = false;
  isLoading$!: Observable<boolean>;
  canvasHintsExpanded$!: Observable<boolean>;

  // Custom palette editing
  newPaletteName = '';
  editingCustomId: string | null = null;
  newCustomColor = '#ff0000';

  constructor(private store: Store<AppState>) {}

  ngOnInit(): void {
    this.store.dispatch(SettingsActions.loadSettings());
    this.store.pipe(
      select(selectIsSettingLoading),
      filter(l => l === false),
      switchMap(() => this.store.pipe(select(selectSettings))),
      take(1)
    ).subscribe(s => {
      this.selectedSetting = {
        ...s,
        activePaletteId: s.activePaletteId ?? 'dawnbringer16',
        paletteColorCount: s.paletteColorCount ?? 16,
        gridLineWidth: s.gridLineWidth ?? 1,
        undoHistoryDepth: s.undoHistoryDepth ?? 50,
        onionSkinEnabled: s.onionSkinEnabled ?? false,
        onionSkinFrames: s.onionSkinFrames ?? 2,
        onionSkinOpacity: s.onionSkinOpacity ?? 20,
        fps: s.fps ?? 12,
        customPalettes: s.customPalettes ?? []
      };
    });
    this.isLoading$ = this.store.pipe(select(selectIsSettingLoading), map(v => v ?? false));
    this.canvasHintsExpanded$ = this.store.select(selectCanvasHintsExpanded);
  }

  onCanvasHintsExpandedChange(expanded: boolean): void {
    this.store.dispatch(UserConfigActions.setCanvasHintsExpanded({ expanded }));
  }

  selectPalette(id: string): void {
    const p = this.builtinPalettes.find(b => b.id === id)
           ?? this.selectedSetting.customPalettes?.find(c => c.id === id);
    this.selectedSetting = {
      ...this.selectedSetting,
      activePaletteId: id,
      paletteColorCount: p ? Math.min(p.colors.length, this.selectedSetting.paletteColorCount ?? p.colors.length) : this.selectedSetting.paletteColorCount
    };
  }

  getActivePalette(): ColorPalettePreset | CustomPalette | undefined {
    return this.builtinPalettes.find(p => p.id === this.selectedSetting?.activePaletteId)
        ?? this.selectedSetting?.customPalettes?.find(p => p.id === this.selectedSetting?.activePaletteId);
  }

  getActivePaletteColors(): string[] {
    const p = this.getActivePalette();
    if (!p) return [];
    return p.colors.slice(0, this.selectedSetting.paletteColorCount ?? p.colors.length);
  }

  // ── Custom palettes ──
  addCustomPalette(): void {
    if (!this.newPaletteName.trim()) return;
    const id = 'custom_' + Date.now();
    const newP: CustomPalette = { id, name: this.newPaletteName.trim(), colors: [] };
    this.selectedSetting = {
      ...this.selectedSetting,
      customPalettes: [...(this.selectedSetting.customPalettes ?? []), newP],
      activePaletteId: id
    };
    this.newPaletteName = '';
    this.editingCustomId = id;
  }

  deleteCustomPalette(id: string): void {
    const remaining = (this.selectedSetting.customPalettes ?? []).filter(p => p.id !== id);
    this.selectedSetting = {
      ...this.selectedSetting,
      customPalettes: remaining,
      activePaletteId: this.selectedSetting.activePaletteId === id ? 'dawnbringer16' : this.selectedSetting.activePaletteId
    };
    if (this.editingCustomId === id) this.editingCustomId = null;
  }

  addColorToCustom(id: string): void {
    const palettes = (this.selectedSetting.customPalettes ?? []).map(p =>
      p.id === id ? { ...p, colors: [...p.colors, this.newCustomColor] } : p
    );
    this.selectedSetting = { ...this.selectedSetting, customPalettes: palettes };
  }

  removeColorFromCustom(paletteId: string, idx: number): void {
    const palettes = (this.selectedSetting.customPalettes ?? []).map(p =>
      p.id === paletteId ? { ...p, colors: p.colors.filter((_, i) => i !== idx) } : p
    );
    this.selectedSetting = { ...this.selectedSetting, customPalettes: palettes };
  }

  save(): void {
    this.store.dispatch(SettingsActions.updateSettings({ settings: this.selectedSetting }));
    this.isSaved = true;
    setTimeout(() => this.isSaved = false, 2000);
  }
}
